/**
 * RadioField Component - Radio button group
 */

import React, { useCallback } from 'react';
import { RadioFieldProps, RadioOption } from '../../../types/forms';
import { BaseField } from './BaseField';

export const RadioField: React.FC<RadioFieldProps> = ({
  name,
  label,
  helpText,
  required = false,
  disabled = false,
  readonly = false,
  error,
  className = '',
  value,
  options,
  orientation = 'vertical',
  onChange,
  onBlur,
  onFocus,
}) => {
  const fieldId = `field-${name}`;

  const handleChange = useCallback(
    (optionValue: string) => {
      if (!disabled && !readonly) {
        onChange?.(optionValue);
      }
    },
    [disabled, readonly, onChange]
  );

  return (
    <BaseField
      name={name}
      label={label}
      helpText={helpText}
      required={required}
      disabled={disabled}
      readonly={readonly}
      error={error}
      className={className}
      fieldId={fieldId}
    >
      <div
        className={`flex ${
          orientation === 'horizontal' ? 'flex-row gap-4' : 'flex-col gap-2'
        }`}
        role="radiogroup"
        aria-labelledby={label ? `${fieldId}-label` : undefined}
        aria-required={required}
        aria-invalid={!!error}
      >
        {options.map((option) => (
          <RadioOptionComponent
            key={option.value}
            name={name}
            option={option}
            checked={value === option.value}
            disabled={disabled || option.disabled}
            onChange={() => handleChange(option.value)}
            onBlur={onBlur}
            onFocus={onFocus}
          />
        ))}
      </div>
    </BaseField>
  );
};

// Individual radio option component
interface RadioOptionComponentProps {
  name: string;
  option: RadioOption;
  checked: boolean;
  disabled?: boolean;
  onChange: () => void;
  onBlur?: () => void;
  onFocus?: () => void;
}

const RadioOptionComponent: React.FC<RadioOptionComponentProps> = ({
  name,
  option,
  checked,
  disabled,
  onChange,
  onBlur,
  onFocus,
}) => {
  const optionId = `${name}-${option.value}`;

  const radioClasses = `
    w-4 h-4
    text-blue-600
    border-gray-300
    focus:ring-2 focus:ring-blue-500
    ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}
  `;

  return (
    <label
      htmlFor={optionId}
      className={`flex items-start ${
        disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
      }`}
    >
      <input
        id={optionId}
        name={name}
        type="radio"
        value={option.value}
        checked={checked}
        disabled={disabled}
        className={radioClasses}
        onChange={onChange}
        onBlur={onBlur}
        onFocus={onFocus}
      />
      <div className="ml-2">
        <span className="text-sm text-gray-700">{option.label}</span>
        {option.helpText && (
          <p className="text-xs text-gray-500 mt-0.5">{option.helpText}</p>
        )}
      </div>
    </label>
  );
};

export default RadioField;